<template>
  <UModal
    v-model:open="open"
    :content="{ dir: 'rtl' }"
    :ui="{
      overlay: 'z-[80]',
      content: [
        'z-[80] w-[calc(100vw-1.5rem)] max-w-md overflow-hidden rounded-2xl ring-1 ring-[var(--ui-border)]',
        'max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:max-h-[92dvh]',
        'max-sm:data-[state=open]:animate-[slide-in-from-bottom_250ms_ease-out] max-sm:data-[state=closed]:animate-[slide-out-to-bottom_200ms_ease-in]',
      ].join(' '),
      header: 'items-start gap-3 border-b border-[var(--ui-border)] bg-[var(--arad-surface)] px-5 py-4 sm:px-6 sm:py-5',
      body: 'space-y-5 bg-[var(--arad-surface)] px-5 py-5 sm:px-6',
      footer: 'block border-t border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/40 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6',
    }"
  >
    <template #title>
      <div class="flex min-w-0 items-start gap-3">
        <div
          class="mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"
          aria-hidden="true"
        >
          <UIcon
            name="i-lucide-package"
            class="size-5"
          />
        </div>
        <div class="min-w-0 space-y-1">
          <p class="text-[11px] font-medium tracking-wide text-[var(--ui-text-muted)]">
            خرید پکیج
          </p>
          <h2 class="truncate text-base font-semibold leading-6 text-[var(--arad-text)] sm:text-lg">
            {{ pkg?.title || 'در حال بارگذاری…' }}
          </h2>
        </div>
      </div>
    </template>

    <template #body>
      <div
        v-if="!pkg"
        class="space-y-4"
      >
        <USkeleton class="h-8 w-40 rounded-full" />
        <USkeleton class="h-16 w-full rounded-2xl" />
        <USkeleton class="h-24 w-full rounded-2xl" />
      </div>

      <div
        v-else
        class="space-y-5"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span class="inline-flex items-center gap-1.5 rounded-full bg-[var(--ui-bg-muted)] px-3 py-1 text-xs font-medium text-[var(--arad-text)]">
            <UIcon
              name="i-lucide-layers"
              class="size-3.5 text-[var(--ui-text-muted)]"
            />
            {{ toFaDigits(live.sessions) }} جلسه
          </span>
          <span
            v-if="paymentModeLabel"
            class="inline-flex items-center gap-1.5 rounded-full bg-[var(--ui-bg-muted)] px-3 py-1 text-xs font-medium text-[var(--ui-text-muted)]"
          >
            <UIcon
              name="i-lucide-wallet"
              class="size-3.5"
            />
            {{ paymentModeLabel }}
          </span>
        </div>

        <p
          v-if="pkg.description"
          class="text-sm leading-7 text-[var(--ui-text-muted)]"
        >
          {{ pkg.description }}
        </p>

        <div
          v-if="pkg.fields.length"
          class="space-y-3"
        >
          <p class="text-xs font-semibold text-[var(--arad-text)]">
            گزینه‌های پکیج
          </p>
          <div
            v-for="field in pkg.fields"
            :key="field.id"
            class="rounded-2xl border border-[var(--ui-border)] bg-[var(--arad-surface)] p-3.5"
          >
            <UFormField
              :label="field.label"
              :required="field.isRequired"
            >
              <UInput
                v-if="field.inputType === 'number'"
                v-model.number="numberValues[field.id]"
                type="number"
                size="lg"
                :min="field.min ?? undefined"
                :max="field.max ?? undefined"
                :ui="{ base: 'text-start' }"
              >
                <template
                  v-if="field.unitLabel"
                  #trailing
                >
                  <span class="text-xs text-[var(--ui-text-muted)]">{{ field.unitLabel }}</span>
                </template>
              </UInput>
              <USelect
                v-else-if="field.inputType === 'select'"
                v-model="selectValues[field.id]"
                size="lg"
                :items="field.options.map((option: { id: string; label: string }) => ({ label: option.label, value: option.id }))"
              />
              <USelectMenu
                v-else
                v-model="multiValues[field.id]"
                multiple
                size="lg"
                :items="field.options.map((option: { id: string; label: string }) => ({ label: option.label, value: option.id }))"
              />
            </UFormField>
          </div>
        </div>

        <div class="space-y-2 rounded-2xl border border-[var(--ui-border)] p-3.5">
          <UFormField label="کد تخفیف">
            <div class="flex gap-2">
              <UInput
                v-model="couponCode"
                dir="ltr"
                class="min-w-0 flex-1"
                placeholder="کد تخفیف"
              />
              <UButton
                variant="soft"
                :loading="couponApplying"
                :disabled="!couponCode.trim() || couponApplying"
                @click="previewCoupon"
              >
                بررسی کد
              </UButton>
            </div>
          </UFormField>
          <p
            v-if="couponError"
            class="text-xs text-error"
          >
            {{ couponError }}
          </p>
          <p
            v-else-if="couponAppliedCode"
            class="text-xs text-success"
          >
            {{ formatRialFa(couponDiscount) }} تخفیف برای این پکیج اعمال می‌شود.
          </p>
        </div>

        <div class="overflow-hidden rounded-2xl border border-[var(--ui-border)] bg-gradient-to-l from-[var(--ui-bg-muted)]/80 to-[var(--arad-surface)]">
          <div class="flex items-end justify-between gap-4 px-4 py-4 sm:px-5">
            <div class="min-w-0 space-y-1">
              <p class="text-xs font-medium text-[var(--ui-text-muted)]">
                مبلغ قابل پرداخت
              </p>
              <p class="text-[1.65rem] font-bold leading-none tracking-tight tabular-nums text-[var(--arad-text)]">
                {{ formatRialFa(finalPrice) }}
              </p>
            </div>
            <div
              class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
              aria-hidden="true"
            >
              <UIcon
                name="i-lucide-receipt"
                class="size-5"
              />
            </div>
          </div>
        </div>

        <p
          v-if="saving"
          class="flex items-center gap-2 text-sm text-[var(--ui-text-muted)]"
        >
          <UIcon
            name="i-lucide-loader-circle"
            class="size-4 animate-spin"
          />
          در حال ثبت خرید و زمان‌بندی جلسات…
        </p>
      </div>
    </template>

    <template
      v-if="pkg"
      #footer
    >
      <div class="flex flex-col gap-2.5">
        <PaymentsGatewayChoice
          v-if="showOnline"
          v-model="paymentGateway"
          :gateways="gateways"
        />
        <UButton
          v-if="showOnline"
          size="lg"
          block
          icon="i-lucide-credit-card"
          :loading="saving"
          @click="checkout(false)"
        >
          پرداخت آنلاین
        </UButton>
        <UButton
          v-if="showOnsite"
          size="lg"
          block
          color="neutral"
          variant="outline"
          icon="i-lucide-building-2"
          :loading="saving"
          @click="checkout(true)"
        >
          پرداخت حضوری
        </UButton>
        <button
          type="button"
          class="mt-0.5 py-2 text-sm text-[var(--ui-text-muted)] transition hover:text-[var(--arad-text)]"
          :disabled="saving"
          @click="open = false"
        >
          انصراف
        </button>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computePackagePrice } from '~/utils/servicePackage/pricing'
import { formatRialFa } from '~/utils/money'
import { toFaDigits } from '~/utils/faDigits'
import type { PricingField } from '~/types/servicePackage'

const props = withDefaults(defineProps<{
  packageId: string | null
  returnTo?: string
}>(), {
  returnTo: '/services',
})
const emit = defineEmits<{ 'update:packageId': [value: string | null] }>()
const toast = useToast()

const open = computed({
  get: () => Boolean(props.packageId),
  set: value => emit('update:packageId', value ? props.packageId : null),
})

const { data } = await useFetch<{ item: {
  id: string
  title: string
  description: string
  basePrice: number
  paymentConfig: { mode: string }
  items: Array<{ sessionCount: number; priceDelta: number }>
  fields: PricingField[]
} }>(() => props.packageId ? `/api/campaign/packages/${props.packageId}` : '', {
  watch: [() => props.packageId],
  immediate: true,
})
const pkg = computed(() => data.value?.item ?? null)
const numberValues = reactive<Record<string, number>>({})
const selectValues = reactive<Record<string, string>>({})
const multiValues = reactive<Record<string, string[]>>({})

watch(pkg, (item) => {
  if (!item) return
  for (const field of item.fields ?? []) {
    if (field.inputType === 'number') numberValues[field.id] = field.min ?? 0
    else if (field.inputType === 'select') {
      selectValues[field.id] = field.options.find(option => option.isDefault)?.id || field.options[0]?.id || ''
    }
    else {
      multiValues[field.id] = field.options.filter(option => option.isDefault).map(option => option.id)
    }
  }
}, { immediate: true })

const selections = computed(() => (pkg.value?.fields ?? []).map((field) => {
  if (field.inputType === 'number') return { fieldId: field.id, value: numberValues[field.id] ?? 0 }
  if (field.inputType === 'multiselect') return { fieldId: field.id, optionIds: multiValues[field.id] ?? [] }
  return { fieldId: field.id, optionIds: selectValues[field.id] ? [selectValues[field.id]] : [] }
}))

const couponCode = ref('')
const couponAppliedCode = ref('')
const couponDiscount = ref(0)
const couponApplying = ref(false)
const couponError = ref('')

const live = computed(() => {
  if (!pkg.value) return { final: 0, sessions: 1, breakdown: [] }
  return computePackagePrice({
    basePrice: pkg.value.basePrice,
    items: pkg.value.items,
    fields: pkg.value.fields,
    selections: selections.value,
  })
})

const finalPrice = computed(() => Math.max(0, live.value.final - couponDiscount.value))

watch(couponCode, () => {
  couponAppliedCode.value = ''
  couponDiscount.value = 0
  couponError.value = ''
})

watch(() => live.value.final, () => {
  couponAppliedCode.value = ''
  couponDiscount.value = 0
  couponError.value = ''
})

const showOnline = computed(() => pkg.value?.paymentConfig.mode !== 'onsite')
const showOnsite = computed(() => {
  const mode = pkg.value?.paymentConfig.mode
  return mode === 'onsite' || mode === 'full_online'
})

const paymentModeLabel = computed(() => {
  const mode = pkg.value?.paymentConfig.mode
  if (mode === 'onsite') return 'فقط حضوری'
  if (mode === 'per_session_online') return 'پرداخت جلسه‌ای'
  if (mode === 'full_online') return 'آنلاین یا حضوری'
  return ''
})

async function previewCoupon() {
  couponError.value = ''
  couponAppliedCode.value = ''
  couponDiscount.value = 0
  const code = couponCode.value.trim()
  if (!code || live.value.final <= 0 || !props.packageId) return
  couponApplying.value = true
  try {
    const result = await $fetch<{ item: { discount: number } }>('/api/campaign/coupons/apply', {
      method: 'POST',
      body: {
        code,
        packageId: props.packageId,
        amount: live.value.final,
        claim: false,
      },
    })
    couponDiscount.value = result.item.discount
    couponAppliedCode.value = code
  }
  catch (error) {
    couponError.value = String((error as { data?: { statusMessage?: string } })?.data?.statusMessage || 'کد تخفیف معتبر نیست')
  }
  finally {
    couponApplying.value = false
  }
}

const saving = ref(false)
const gateways = ref<Array<{ provider: string; label: string }>>([])
const paymentGateway = ref('')

watch(() => props.packageId, async (id) => {
  if (!id) return
  couponCode.value = ''
  couponAppliedCode.value = ''
  couponDiscount.value = 0
  couponError.value = ''
  const data = await $fetch<{ items: Array<{ provider: string; label: string }> }>('/api/payments/gateways').catch(() => ({ items: [] }))
  gateways.value = data.items
  paymentGateway.value = data.items[0]?.provider || ''
}, { immediate: true })

async function checkout(payOnsite: boolean) {
  saving.value = true
  try {
    const me = await $fetch<{ item?: { patient?: { id: string } } }>('/api/patient/me').catch(() => null)
    const patientId = me?.item?.patient?.id
    if (!patientId) {
      const back = props.packageId
        ? `${props.returnTo}?package=${encodeURIComponent(props.packageId)}`
        : props.returnTo
      await navigateTo(`/patient/login?redirect=${encodeURIComponent(back)}`)
      return
    }
    const result = await $fetch<{ paymentUrl?: string | null }>(`/api/campaign/packages/${props.packageId}/checkout`, {
      method: 'POST',
      body: {
        patientId,
        selections: selections.value,
        couponCode: couponAppliedCode.value || undefined,
        payOnsite,
        provider: payOnsite ? undefined : (paymentGateway.value || undefined),
      },
    })
    if (result.paymentUrl) {
      window.location.href = result.paymentUrl
      return
    }
    await navigateTo('/patient/packages?paid=1')
  }
  catch (error) {
    toast.add({ title: 'خرید پکیج ممکن نشد', description: String((error as { data?: { statusMessage?: string } })?.data?.statusMessage || ''), color: 'error' })
  }
  finally {
    saving.value = false
  }
}
</script>
